/**
 * Tap alternatives for dragging a project on the Timeline (plans/mobile-v2.md §3.5):
 * "Move to Planned / Active / Done" instead of dragging it between Focus
 * columns, and "Schedule…" instead of dragging it to another month or week.
 */
import { router } from '@inertiajs/svelte';
import { toast } from 'svelte-sonner';
import type { Project, ProjectStatus } from './types';
import type { SheetAction } from './sheetActions';
import { openProjectScheduleModal } from './stores/ui';

/** The Focus columns, in board order. */
export const FOCUS_STATUSES: { status: ProjectStatus; label: string }[] = [
  { status: 'planned', label: 'Planned' },
  { status: 'active', label: 'Active' },
  { status: 'done', label: 'Done' },
];

/** The Focus column a status is shown in: "almost" projects sit with Active. */
export function focusColumn(status: ProjectStatus): ProjectStatus {
  return status === 'almost' ? 'active' : status;
}

/** Accent colour of a Focus column: Planned blue, Active amber, Done green. */
export const FOCUS_ACCENTS = { planned: '#3b82f6', active: '#f59e0b', done: '#22c55e' } as const;

/** The accent of the column a project's status is shown in ("almost" is Active). */
export function focusAccent(status: ProjectStatus): string {
  return FOCUS_ACCENTS[focusColumn(status) as keyof typeof FOCUS_ACCENTS];
}

/** Sets a project's status (project metadata, so via Inertia: the page's props refresh). */
export function moveProjectToStatus(projectId: string, status: ProjectStatus) {
  router.patch(
    `/projects/${projectId}/update_status`,
    { status },
    {
      preserveScroll: true,
      preserveState: true,
      onError: () => {
        toast.error('Failed to update status');
      },
    },
  );
}

/**
 * Action-sheet rows for a project: "Move to …" for each Focus column it isn't
 * in, then "Schedule…" (ProjectModal's schedule mode).
 */
export function projectMoveActions(project: Project): SheetAction[] {
  const current = focusColumn(project.status);
  const moves: SheetAction[] = FOCUS_STATUSES.filter(({ status }) => status !== current).map(
    ({ status, label }) => ({
      label: `Move to ${label}`,
      icon: 'arrow-right',
      onSelect: () => moveProjectToStatus(project.id, status),
    }),
  );
  return [
    ...moves,
    { label: 'Schedule…', icon: 'calendar', onSelect: () => openProjectScheduleModal(project) },
  ];
}
